export const digitsOnly = (value: string) => value.replace(/\D/g, "");

export function formatEmailInput(value: string) {
  return value.replace(/\s/g, "").toLowerCase().slice(0, 254);
}

export function formatIdentifierCode(value: string) {
  return value
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toUpperCase()
    .replace(/[^A-Z0-9_-]+/g, "_")
    .replace(/_+/g, "_")
    .replace(/^[^A-Z0-9]+/, "")
    .slice(0, 30);
}

export function finalizeIdentifierCode(value: string) {
  return formatIdentifierCode(value).replace(/[_-]+$/, "");
}

export function formatBrazilianPhone(value: string) {
  let digits = digitsOnly(value);
  if (digits.length > 11 && digits.startsWith("55")) digits = digits.slice(2);
  digits = digits.slice(0, 11);
  if (!digits) return "";
  if (digits.length <= 2) return `(${digits}`;
  const areaCode = digits.slice(0, 2);
  const local = digits.slice(2);
  const prefixLength = digits.length === 11 ? 5 : 4;
  if (local.length <= prefixLength) return `(${areaCode}) ${local}`;
  return `(${areaCode}) ${local.slice(0, prefixLength)}-${local.slice(prefixLength)}`;
}

export function formatBrazilianTaxId(value: string, kind: "individual" | "company") {
  const limit = kind === "company" ? 14 : 11;
  const digits = digitsOnly(value).slice(0, limit);
  if (kind === "individual") {
    return digits
      .replace(/^(\d{3})(\d)/, "$1.$2")
      .replace(/^(\d{3})\.(\d{3})(\d)/, "$1.$2.$3")
      .replace(/^(\d{3})\.(\d{3})\.(\d{3})(\d)/, "$1.$2.$3-$4");
  }
  return digits
    .replace(/^(\d{2})(\d)/, "$1.$2")
    .replace(/^(\d{2})\.(\d{3})(\d)/, "$1.$2.$3")
    .replace(/^(\d{2})\.(\d{3})\.(\d{3})(\d)/, "$1.$2.$3/$4")
    .replace(/^(\d{2})\.(\d{3})\.(\d{3})\/(\d{4})(\d)/, "$1.$2.$3/$4-$5");
}

export function formatBrazilianPostalCode(value: string) {
  const digits = digitsOnly(value).slice(0, 8);
  return digits.length > 5 ? `${digits.slice(0, 5)}-${digits.slice(5)}` : digits;
}

export function brazilianPhoneToE164(value: string) {
  let digits = digitsOnly(value);
  if (digits.length > 11 && digits.startsWith("55")) digits = digits.slice(2);
  return digits.length === 10 || digits.length === 11 ? `+55${digits}` : null;
}

const digitFor = (base: string, weights: number[]) => {
  const total = base.split("").reduce((sum, digit, index) => sum + Number(digit) * weights[index]!, 0);
  const remainder = total % 11;
  return remainder < 2 ? 0 : 11 - remainder;
};

export function isValidBrazilianTaxId(value: string, kind: "individual" | "company") {
  const digits = digitsOnly(value);
  if (/^(\d)\1+$/.test(digits)) return false;
  if (kind === "individual") {
    if (digits.length !== 11) return false;
    const first = digitFor(digits.slice(0, 9), [10, 9, 8, 7, 6, 5, 4, 3, 2]);
    const second = digitFor(`${digits.slice(0, 9)}${first}`, [11, 10, 9, 8, 7, 6, 5, 4, 3, 2]);
    return digits.endsWith(`${first}${second}`);
  }
  if (digits.length !== 14) return false;
  const first = digitFor(digits.slice(0, 12), [5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2]);
  const second = digitFor(`${digits.slice(0, 12)}${first}`, [6, 5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2]);
  return digits.endsWith(`${first}${second}`);
}
